import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AUDIT } from "@/constants/testIds";
import api from "@/lib/api";
import { ShieldCheck, User, Clock, Globe } from "lucide-react";

const ACTION_LABELS = {
  login: "Inicio de sesión",
  logout: "Cierre de sesión",
  create_template: "Creó plantilla",
  update_template: "Modificó plantilla",
  delete_template: "Eliminó plantilla",
  restore_template_version: "Restauró versión",
  assign_template: "Asignó plantilla",
  sync_users: "Sincronizó usuarios",
  create_assignment: "Creó regla de asignación",
  delete_assignment: "Eliminó regla de asignación",
  publish_signatures: "Publicó firmas",
  update_graph_settings: "Actualizó config. Graph",
  update_smtp_settings: "Actualizó config. SMTP",
  update_branding: "Actualizó identidad",
  update_sync_settings: "Actualizó config. sincronización",
  create_image: "Añadió imagen",
  delete_image: "Eliminó imagen",
  send_test_mail: "Envió correo de prueba",
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/audit-logs", { params: { limit: 200 } })
      .then((r) => setLogs(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-1">
          Seguridad
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Auditoría</h1>
        <p className="text-sm text-slate-500 mt-1">
          Registro de acciones administrativas. {logs.length} eventos.
        </p>
      </div>

      <Card className="border-slate-200 shadow-sm" data-testid={AUDIT.list}>
        {loading && (
          <div className="p-8 text-center text-sm text-slate-400">Cargando...</div>
        )}
        <div className="divide-y divide-slate-200">
          {logs.map((l) => (
            <div key={l.id} className="p-4 hover:bg-slate-50 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-md bg-orange-100 text-orange-700 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium text-slate-900">
                      {ACTION_LABELS[l.action] || l.action}
                    </div>
                    {l.entity_name && (
                      <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-slate-200 text-[10px]">
                        {l.entity_name}
                      </Badge>
                    )}
                    {l.result === "failure" && (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200 text-[10px]">
                        Fallido
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500 mt-1 flex-wrap">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" strokeWidth={1.75} />
                      {l.admin_user}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" strokeWidth={1.75} />
                      {new Date(l.created_at).toLocaleString("es")}
                    </span>
                    {l.ip_address && (
                      <span className="flex items-center gap-1 font-mono">
                        <Globe className="w-3 h-3" strokeWidth={1.75} />
                        {l.ip_address}
                      </span>
                    )}
                  </div>
                  {l.changes && Object.keys(l.changes).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-[11px] text-slate-400 cursor-pointer hover:text-slate-600">
                        Ver detalles
                      </summary>
                      <pre className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded text-[10px] font-mono text-slate-700 overflow-auto">
                        {JSON.stringify(l.changes, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))}
          {!loading && logs.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-400">Sin eventos registrados.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
